import { useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePermissions, useSite, useModal, useAuthContext, useTimeZonedFunctions } from '/src/hooks';
import { useNavigate } from 'react-router-dom';

import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { SitePickerBar } from '/src/components/site-picker/index';
import { InstallationBanner } from '/src/components/banners/InstallationBanner';
import { PaymentAlerts } from '/src/features/_global/components/PaymentAlerts';
import { FEATURES } from '/src/features/_global/constants';
import { dashboardQuery } from '../queries';
import { SourceSelector } from '/src/components/source-selector/source-selector';
import { buildHomeDateRange } from '../dateRange';
import {
  InstructionalModal,
  GA_FIRST_VISIT_TYPE,
  hasSeenFirstVisitModal,
  setFirstVisitModalSeen,
} from '/src/features/web-analytics/components/modals/InstructionalModal';
import { setUserDataMutation } from '/src/features/_global/mutations';

export function HomeDashboardContent({ dateRange, sections }) {
  const navigate = useNavigate();
  const { sites, selectedSite, loadingSites, reloadSites } = useSite();
  const { currentAccount, currentUser } = useAuthContext();
  const permissions = usePermissions();
  const modal = useModal();
  const { mutate: setUserData } = useMutation(setUserDataMutation);
  const canEditSites = permissions.can('create', FEATURES.SITES).allowed;
  const { getConvertedDateRange, differenceInCalendarDays, subDays } = useTimeZonedFunctions();

  const convertedDateRange = useMemo(
    () => buildHomeDateRange(dateRange, { getConvertedDateRange, differenceInCalendarDays, subDays }),
    [dateRange, getConvertedDateRange, differenceInCalendarDays, subDays],
  );

  const { data, isLoading } = useQuery({
    ...dashboardQuery({
      siteId: selectedSite?.id,
      startAt: convertedDateRange.start,
      endAt: convertedDateRange.end,
      previousStartAt: convertedDateRange.previousStart,
      previousEndAt: convertedDateRange.previousEnd,
    }),
    enabled: Boolean(selectedSite?.id),
  });

  const dashboardData = data?.dashboard;

  useEffect(() => {
    if (isLoading || !selectedSite) {
      return;
    }

    const hasSeenGAModal = hasSeenFirstVisitModal(currentUser, GA_FIRST_VISIT_TYPE.GA_INTRO);
    const hasSeenGAPropertySwitcherModal = hasSeenFirstVisitModal(
      currentUser,
      GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER,
    );

    const hasGaProperties = (currentAccount?.gaProperties?.length ?? 0) > 0;
    const hasEnabledSuccessfullyConnectedGAProperty =
      currentAccount?.gaProperties?.some((prop) => !!prop.enabledAt && !!prop.lastSuccessAt) ?? false;

    if (!hasSeenGAModal && !hasGaProperties) {
      modal.show(
        <InstructionalModal
          type={GA_FIRST_VISIT_TYPE.GA_INTRO}
          onClose={() => {
            setFirstVisitModalSeen(GA_FIRST_VISIT_TYPE.GA_INTRO, setUserData, currentUser);
          }}
          onConfirm={() => {
            navigate('/web-analytics');
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('openSourcePicker'));
            }, 100);
          }}
        />,
      );
      return;
    }

    if (!hasSeenGAPropertySwitcherModal && hasGaProperties && hasEnabledSuccessfullyConnectedGAProperty) {
      modal.show(
        <InstructionalModal
          type={GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER}
          onClose={() => {
            setFirstVisitModalSeen(GA_FIRST_VISIT_TYPE.GA_PROPERTY_SWITCHER, setUserData, currentUser);
          }}
          onConfirm={() => {
            navigate('/web-analytics');
          }}
        />,
      );
    }
  }, [modal, isLoading, selectedSite, navigate, currentAccount, currentUser, setUserData]);

  if (!loadingSites && !sites.length && canEditSites) {
    return (
      <AddSiteModal
        customTitle="Add a site to get started"
        onExtraSteps={() => {
          reloadSites();
        }}
        onCancel={() => {
          navigate('/options', { replace: true });
        }}
      />
    );
  }

  return (
    <>
      <SitePickerBar
        Picker={SourceSelector}
        sites={sites}
        loading={loadingSites}
        addSiteOption={canEditSites}
        pageDisplayName="Home dashboard"
      />
      <PaymentAlerts />
      <>
        <InstallationBanner />
        <div className="flex flex-col p-10">
          <div className="grid grid-flow-row-dense grid-cols-3 gap-5">
            {sections
              .filter((s) => s.enabled)
              .filter((s) => permissions.can('navigate', s.feature)?.allowed)
              .map(({ Component, name }) => (
                <Component key={name} dateRange={convertedDateRange} data={dashboardData} loading={isLoading} />
              ))}
          </div>
        </div>
      </>
    </>
  );
}
